<template>
  <div class="order-management-container">
    <div class="page-header">
      <h2 class="page-title">订单管理</h2>
      <p class="page-subtitle">查看和管理商城的所有用户订单。</p>
    </div>

    <div class="table-card">
      <table>
        <thead>
          <tr>
            <th></th> <th>订单号</th>
            <th>用户信息</th>
            <th>总金额</th>
            <th>订单状态</th>
            <th>下单时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <template v-for="order in orders" :key="order.id">
          <tr class="order-summary-row">
            <td>
              <button @click="toggleDetails(order.id)" class="expand-btn">
                <svg :class="{ 'rotated': expandedOrderIds.has(order.id) }" width="16" height="16" viewBox="0 0 24 24"><path d="m6 9 6 6 6-6"></path></svg>
              </button>
            </td>
            <td class="order-number">{{ order.orderNumber }}</td>
            <td class="user-info">
              <img :src="order.user.avatarUrl || 'https://placehold.co/40x40'" :alt="order.user.username">
              <div>
                <div>{{ order.user.nickname || order.user.username }}</div>
                <div class="user-id">ID: {{ order.user.id }}</div>
              </div>
            </td>
            <td class="total-price">¥{{ order.totalPrice }}</td>
            <td>
              <div class="status-cell">
                <div v-if="order.status === 'REFUND_PENDING'" class="refund-actions">
                    <button @click="approveRefund(order)" class="action-btn approve">同意退款</button>
                    <button @click="denyRefund(order)" class="action-btn deny">拒绝</button>
                </div>
                <button v-else-if="order.status === 'PAID'" @click="shipOrder(order)" class="action-btn ship">
                  发货
                </button>
                <span v-else :class="['status-tag', order.status.toLowerCase()]">
                  {{ formatStatus(order.status) }}
                </span>
              </div>
            </td>
            <td>{{ formatTimeAsIs(order.createdAt) }}</td>
            <td class="actions-cell">
              <button @click="confirmDelete(order.id)" class="action-btn delete">删除</button>
            </td>
          </tr>
          <tr v-if="expandedOrderIds.has(order.id)" class="order-details-row">
            <td colspan="7">
              <div class="order-details-content">
                <div class="detail-section">
                  <strong>收货信息:</strong>
                  <p>{{ order.address.recipientName }}, {{ order.address.recipientPhone }}</p>
                  <p>{{ formatAddress(order.address) }}</p>
                </div>
                <div class="detail-section">
                  <strong>商品列表:</strong>
                  <ul>
                    <li v-for="item in order.orderItems" :key="item.id">
                      {{ item.productName }} - (¥{{ item.price }} x {{ item.quantity }})
                    </li>
                  </ul>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue';

const { appContext } = getCurrentInstance();
const $axios = appContext.config.globalProperties.$axios;
const $notify = appContext.config.globalProperties.$notify;
const $confirm = appContext.config.globalProperties.$confirm;

const orders = ref([]);
const expandedOrderIds = ref(new Set());

/**
 * 【核心修正】这是一个不进行时区转换的函数
 * 它会直接格式化数据库中的原始时间值
 * @param {string} isoString - 从后端获取的时间字符串
 */
const formatTimeAsIs = (isoString) => {
  if (!isoString) return 'N/A';
  
  const date = new Date(isoString);

  if (isNaN(date.getTime())) {
    return '时间格式无效';
  }
  
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // 月份是从0开始的
  const day = date.getUTCDate();
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');

  let ampm = '上午';
  if (hours >= 12) {
    ampm = '下午';
    if (hours > 12) {
      hours -= 12;
    }
  }
  if (hours === 0) {
    hours = 12;
  }

  return `${year}/${month}/${day} ${ampm}${hours}:${minutes}:${seconds}`;
};

const fetchOrders = async () => {
  try {
    const res = await $axios.get('/admin/orders');
    orders.value = res.data.data;
  } catch (error) {
    console.error("获取订单列表失败:", error);
    $notify.add('获取订单列表失败', 'error');
  }
};

const toggleDetails = (orderId) => {
  if (expandedOrderIds.value.has(orderId)) {
    expandedOrderIds.value.delete(orderId);
  } else {
    expandedOrderIds.value.add(orderId);
  }
};

const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.country || ''} ${addr.region || ''} ${addr.street || ''} ${addr.houseNumber || ''}`.trim();
};

const formatStatus = (status) => {
  const statusMap = {
    UNPAID: '未支付',
    PAID: '待发货',
    SHIPPED: '待收货',
    RECEIVED: '已收货',
    COMPLETED: '已完成',
    REFUND_PENDING: '退款中',
    REFUNDED: '已退款'
  };
  return statusMap[status] || status;
};

const shipOrder = async (order) => {
  $confirm.show({
      message: `确定要将订单 #${order.orderNumber} 的状态更新为“已发货”吗？`,
      onConfirm: async () => {
        try {
          await $axios.patch(`/admin/orders/${order.id}/status`, { status: 'SHIPPED' });
          $notify.add(`订单 #${order.orderNumber} 已发货`, 'success');
          await fetchOrders(); 
        } catch (error) {
          console.error("更新订单状态失败:", error);
          $notify.add('更新订单状态失败', 'error');
        }
      }
  });
};

const approveRefund = (order) => {
    $confirm.show({
        message: `确定同意订单 #${order.orderNumber} 的退款申请吗？商品库存将归还。`,
        onConfirm: async () => {
            try {
                await $axios.put(`/admin/orders/${order.id}/approve-refund`);
                $notify.add('退款已批准', 'success');
                await fetchOrders();
            } catch (error) {
                $notify.add(error.response?.data?.msg || '操作失败', 'error');
            }
        }
    });
};

const denyRefund = (order) => {
    $confirm.show({
        message: `确定拒绝订单 #${order.orderNumber} 的退款申请吗？订单将恢复为“待发货”状态。`,
        onConfirm: async () => {
            try {
                await $axios.put(`/admin/orders/${order.id}/deny-refund`);
                $notify.add('退款已拒绝', 'success');
                await fetchOrders();
            } catch (error) {
                $notify.add(error.response?.data?.msg || '操作失败', 'error');
            }
        }
    });
};

const confirmDelete = (orderId) => {
  $confirm.show({
    message: '【高危操作】确定要永久删除此订单吗？此操作不可恢复。',
    onConfirm: async () => {
      try {
        await $axios.delete(`/admin/orders/${orderId}`);
        $notify.add('订单删除成功', 'success');
        await fetchOrders();
      } catch (error) {
        console.error("删除订单失败:", error);
        $notify.add('删除订单失败', 'error');
      }
    }
  });
};

onMounted(fetchOrders);
</script>


<style scoped>
.expand-btn { background: none; border: none; cursor: pointer; padding: 5px; display: flex; align-items: center; justify-content: center; }
.expand-btn svg { transition: transform 0.2s ease-in-out; }
.expand-btn svg.rotated { transform: rotate(180deg); }
.order-details-row td { padding: 0; border-top: none; background-color: #fafcff; }
.order-details-content { display: flex; gap: 40px; padding: 20px 40px 20px 80px; }
.detail-section { font-size: 0.9em; }
.detail-section strong { display: block; margin-bottom: 8px; color: #333; }
.detail-section p, .detail-section ul { margin: 0; padding: 0; list-style: none; color: #666; }
.order-management-container { padding: 20px; }
.page-header { margin-bottom: 25px; }
.page-title { font-size: 1.8em; font-weight: 600; color: #333; }
.page-subtitle { color: #888; margin-top: 5px; }
.table-card { background: #fff; border-radius: 12px; padding: 20px; box-shadow: 0 4px 25px rgba(0,0,0,0.07); overflow-x: auto; }
table { width: 100%; border-collapse: collapse; min-width: 800px; }
th, td { padding: 16px; text-align: left; border-bottom: 1px solid #f0f0f0; vertical-align: middle; }
thead { background-color: #f9fafb; }
th { font-weight: 500; color: #888; font-size: 14px; }
.order-number { font-family: monospace; font-size: 0.9em; color: #555; }
.user-info { display: flex; align-items: center; gap: 10px; }
.user-info img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
.user-id { font-size: 0.8em; color: #999; }
.total-price { font-weight: bold; color: #e44d26; }
.actions-cell { display: flex; gap: 10px; }
.action-btn.delete { border: 1px solid #f56c6c; color: #f56c6c; background-color: #fef0f0; padding: 5px 12px; border-radius: 6px; cursor: pointer; }
.action-btn.delete:hover { background-color: #f56c6c; color: #fff; }
.action-btn.ship { border: 1px solid #52c41a; color: #52c41a; background-color: #f6ffed; padding: 5px 12px; border-radius: 6px; cursor: pointer; font-weight: bold; }
.action-btn.ship:hover { background-color: #52c41a; color: #fff; }
.status-tag { font-weight: bold; font-size: 0.9em; }
.status-tag.unpaid { color: #faad14; }
.status-tag.shipped { color: #1890ff; }
.status-tag.completed { color: #52c41a; }
.status-tag.received { color: #2ecc71; }
.refund-actions { display: flex; gap: 8px; }
.action-btn.approve { background-color: #f0f9eb; color: #67c23a; border: 1px solid #c2e7b0; padding: 5px 12px; border-radius: 6px; cursor: pointer; }
.action-btn.approve:hover { background-color: #67c23a; color: #fff; }
.action-btn.deny { background-color: #fef0f0; color: #f56c6c; border: 1px solid #fbc4c4; padding: 5px 12px; border-radius: 6px; cursor: pointer; }
.action-btn.deny:hover { background-color: #f56c6c; color: #fff; }
.status-tag.refund_pending { color: #909399; }
.status-tag.refunded { color: #b6a2de; }
</style>